import React, { useEffect, useMemo, useState } from 'react';
import { Props } from '../../container';
import { FormikProps } from 'formik';
import { ReportFileNameType, ScheduledEventInput } from 'src/features/reporting/store/types';
import { ReportAttachmentType, ReportFormat, SaveReportingParametersRequest } from 'src/features/clients/client/reportsV2/store/types';
import { DateRange } from 'src/store';
import { WO2Select, WO2ToggleSwitch } from 'src/common';
import { Box, Divider, FormControl, Grid, InputLabel, MenuItem, Typography } from '@mui/material';
import ItemOrdering from 'src/common/components/ItemOrdering';
import WO2Button from 'src/common/components/button/Button';
import { UploadReportAttachment } from 'src/features/clients/client/reportsV2/components/UploadReportAttachment';
import DateRangePicker from 'src/common/components/DateRangePicker';
import ReportingParameterSelect from 'src/features/clients/client/reportsV2/components/ReportingParameterSelect';
import { ReportGenerationCustomNamingModal } from 'src/features/reporting/components/generation/ReportGenerationCustomNamingModal';
import { MergeReportsToggle } from 'src/features/clients/client/reportsV2/components/MergeReportsToggle';
import { ReportFormatOption } from 'src/features/clients/client/reportsV2/components/ReportFormatOption';
import { ReportingComponentList } from 'src/features/clients/client/reportsV2/components/ReportingComponentList';
import { ReportingComponentListOptions } from 'src/features/clients/client/reportsV2/store/constants';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { createInitiateReportRequest, getReportsToSort } from 'src/features/clients/client/reportsV2/store/helper';

interface ReportParameterStepProps extends Props {
  formikProps: FormikProps<ScheduledEventInput>;
  editing: boolean;
}

export const ReportParameterStep = (props: ReportParameterStepProps): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState<ReportAttachmentType>(ReportAttachmentType.None);
  const [fullReports, setFullReports] = useState<boolean>(false);
  const [templates, setTemplates] = useState<boolean>(false);

  const parameters = props.reports.parameters;

  const history = useHistory();
  const location = useLocation();
  const routeMatch = useRouteMatch();

  const customNamingTemplate = useMemo(() => {
    return props.bulk.namingTemplate.map((value) => value.value ?? value.tag).join('_');
  }, [props.bulk.namingTemplate]);

  const updateReportDates = (dates: DateRange) => {
    props.setDateParameter(dates);
  };

  const fetchSavedParametersList = () => {
    const selectedAfsls: number[] = props.formikProps.values.clientSearchFilter?.afslIds ?? [];
    props.fetchReportParametersList({
      kind: 'S',
      afslId: selectedAfsls.length == 1 ? selectedAfsls : [],
    });
  };

  const handleClickIncludeLogo = () => {
    props.setIncludeLogoParameter(!parameters.includeLogo);
  };

  const handleClickIncludeCoverLetter = () => {
    props.setIncludeCoverLetterParameter(!parameters.includeCoverLetter);
    setIsMenuOpen(!parameters.includeCoverLetter);
    setCurrentReportType(ReportAttachmentType.CoverLetter);
  };

  const handleClickIncludeAppendix = () => {
    props.setIncludeAppendixParameter(!parameters.includeAppendix);
    setIsMenuOpen(!parameters.includeAppendix);
    setCurrentReportType(ReportAttachmentType.Appendix);
  };

  const closeUploader = () => {
    setIsMenuOpen(false);

    currentReportType === ReportAttachmentType.CoverLetter ? props.setIncludeCoverLetterParameter(false) : props.setIncludeAppendixParameter(false);
  };

  const setFile = (file: File) => {
    setIsMenuOpen(false);

    if (currentReportType === ReportAttachmentType.CoverLetter) {
      props.saveCoverPageFile(file);
    } else if (currentReportType === ReportAttachmentType.Appendix) {
      props.saveAppendixFile(file);
    }
  };

  useEffect(() => {
    if (!parameters.selectedParameter) return;
    props.getReportParametersById({
      id: parameters.selectedParameter?.id,
    });
  }, [parameters.selectedParameter]);

  useEffect(() => {
    props.setUseTriumphAsDataSourceParameter(!parameters.isDataFromTriumph);
  }, [props.isDataFromTriumph]);

  useEffect(() => {
    props.fetchBenchmarks(-1);
    fetchSavedParametersList();
  }, []);

  useEffect(() => {
    if (props.reports.parameters.mergeReports) {
      if (props.formikProps.values.reportFileNameTemplate === ReportFileNameType.NonMerged.displayName) {
        props.formikProps.setFieldValue('reportFileNameTemplate', ReportFileNameType.Merged.displayName);
      }
    } else {
      if (props.formikProps.values.reportFileNameTemplate === ReportFileNameType.Merged.displayName) {
        props.formikProps.setFieldValue('reportFileNameTemplate', ReportFileNameType.NonMerged.displayName);
      }
    }
  }, [props.reports.parameters.mergeReports]);

  useEffect(() => {
    const { reportRequest } = createInitiateReportRequest({
      isDataFromTriumph: props.isDataFromTriumph,
      parameters: parameters,
      handleClickMergeReports: () => props.setMergeReportsParameter(!parameters.mergeReports),
    });
    const payload: SaveReportingParametersRequest = {
      ...reportRequest,
      visibleToAfsl: props.reports.parameters.reportingParameter.visibleToAfsl,
      parameterName: props.reports.parameters.reportingParameter.parameterName,
    };
    props.formikProps.setFieldValue('reportParameters', payload);
  }, [props.reports.parameters]);

  useEffect(() => {
    if (props.editing) {
      const reportParameters = props.formikProps.values.reportParameters;
      if (reportParameters) {
        props.setReportParameters(reportParameters);
      }
    }
  }, []);

  return (
    <Grid container direction="column" spacing={3}>
      <UploadReportAttachment isOpen={isMenuOpen} handleImport={setFile} handleClose={closeUploader} title={`Upload ${currentReportType}`} />
      <ItemOrdering
        itemsToSort={getReportsToSort(parameters.reportsToSort, parameters.reportFormat)}
        setSelectedReport={props.setSelectedReportOrderParameter}
        setShowItemOrderingModal={props.setShowItemOderingModalParameter}
        generateReport={() => props.setShowItemOderingModalParameter(false)}
        isOpen={parameters.showItemOrdering}
      />

      {templates && (
        <ReportGenerationCustomNamingModal
          clearNamingTemplate={() => {
            props.clearNamingTemplate();
            props.formikProps.setFieldValue(
              'reportFileNameTemplate',
              props.reports.parameters.mergeReports ? ReportFileNameType.Merged.displayName : ReportFileNameType.NonMerged.displayName
            );
          }}
          appendNamingTemplate={props.appendNamingTemplate}
          swapNamingTemplateTags={props.swapNamingTemplateTags}
          setNamingTemplateTagValue={props.setNamingTemplateTagValue}
          namingTemplate={props.bulk.namingTemplate}
          deleteNamingTemplateTag={props.deleteNamingTemplateTag}
          isOpen={templates}
          handleClose={() => setTemplates(false)}
          onConfirm={() => {
            props.formikProps.setFieldValue('reportFileNameTemplate', customNamingTemplate);
          }}
        />
      )}
      {!props.hasClientDirectAccess && (
        <Grid item container spacing={2}>
          <Grid item flex={1}>
            <ReportingParameterSelect
              disabled={props.getReportParametersByIdProgress.isLoading}
              items={props.scheduler.savedParametersList}
              selectedParameter={parameters?.selectedParameter ?? null}
              setParameter={props.setSelectedReportingParameter}
            />
          </Grid>
        </Grid>
      )}
      <Grid item>
        <Divider />
      </Grid>
      <Grid item container spacing={2} alignItems="center" wrap="nowrap">
        <Grid item flex={1}>
          <FormControl fullWidth>
            <InputLabel id="report-file-name">Report File Name</InputLabel>
            <WO2Select
              fullWidth
              labelId="report-file-name"
              label="Report File Name"
              value={props.formikProps.values.reportFileNameTemplate}
              onChange={(e) => props.formikProps.setFieldValue('reportFileNameTemplate', e.target.value)}
            >
              {props.bulk.namingTemplate.length > 0 && (
                <MenuItem divider value={customNamingTemplate}>
                  {customNamingTemplate}
                </MenuItem>
              )}
              {props.reports.parameters.mergeReports ? (
                <MenuItem value={ReportFileNameType.Merged.displayName}>{ReportFileNameType.Merged.displayName}</MenuItem>
              ) : (
                <MenuItem value={ReportFileNameType.NonMerged.displayName}>{ReportFileNameType.NonMerged.displayName}</MenuItem>
              )}
              <MenuItem value={ReportFileNameType.IpsPortfolio.displayName}>{ReportFileNameType.IpsPortfolio.displayName}</MenuItem>
            </WO2Select>
          </FormControl>
        </Grid>
        <Grid item>
          <WO2Button variant="contained" onClick={() => setTemplates(true)}>
            Set naming template
          </WO2Button>
        </Grid>
      </Grid>
      <Grid item container spacing={2} flexWrap="nowrap">
        <Grid item container direction="column" xs={12} md={3} spacing={2}>
          <Grid item>
            <DateRangePicker dateRange={parameters.dates} setDateRange={updateReportDates} inceptionDate={parameters.inceptionDate} />
          </Grid>
          <Grid item>
            <MergeReportsToggle
              mergeReports={parameters.mergeReports}
              setMergeReportsParameter={props.setMergeReportsParameter}
              setShowItemOderingModalParameter={props.setShowItemOderingModalParameter}
              disabled={parameters.reportsToSort.length < 2 || parameters.reportFormat === ReportFormat.JSON}
            />
          </Grid>
          <Grid item container>
            <WO2ToggleSwitch
              checked={parameters.includeLogo}
              onChange={handleClickIncludeLogo}
              color="primary"
              name="includeLogo"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
            <Typography variant="h4" style={{ paddingTop: '10px' }}>
              Include Logo
            </Typography>
          </Grid>
          <Grid item container>
            <WO2ToggleSwitch
              checked={parameters.includeCoverLetter}
              onChange={handleClickIncludeCoverLetter}
              color="primary"
              name="includeCoverPage"
              inputProps={{ 'aria-label': 'primary checkbox' }}
              disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
            />
            <Typography variant="h4" style={{ paddingTop: '10px' }}>
              Cover Page
            </Typography>
          </Grid>
          <Grid item container>
            <WO2ToggleSwitch
              checked={parameters.includeAppendix}
              onChange={handleClickIncludeAppendix}
              color="primary"
              name="includeAppendix"
              inputProps={{ 'aria-label': 'primary checkbox' }}
              disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
            />
            <Typography variant="h4" style={{ paddingTop: '10px' }}>
              Appendix
            </Typography>
          </Grid>
        </Grid>
        <Grid item container display="flex" direction="column" xs={12} md={9} spacing={1}>
          <Grid item container spacing={2} alignItems="center" mb={1}>
            <Grid item>
              <WO2Button variant="contained" onClick={props.expandAll}>
                Expand
              </WO2Button>
            </Grid>
            <Grid item>
              <ReportFormatOption
                format={parameters.reportFormat}
                setReportFormat={props.setReportFormat}
                hasClientEditAdminOnlyPermission={props.hasClientEditAdminOnlyPermission}
                isDisplayOnly={false}
              />
            </Grid>
            {props.hasClientEditAdminOnlyPermission && (
              <Grid item>
                <Box display="flex" alignItems="center">
                  <WO2ToggleSwitch checked={fullReports} onChange={(e) => setFullReports(e.target.checked)} color="primary" />
                  <Typography variant="h4">Full Reports</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
          <ReportingComponentList
            history={history}
            location={location}
            match={routeMatch}
            {...props}
            ignoreClientSubCheck
            reportingList={ReportingComponentListOptions}
            fullReports={fullReports}
            allowConsolidation
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
